import {
  Directive,
  Input,
  OnInit,
  TemplateRef,
  ViewContainerRef,
  IterableDiffers,
  Inject,
  OnDestroy
} from '@angular/core';
import {
  NgForOf,
  NgForOfContext
} from '@angular/common';
import {
  FormGroupState,
  FormArrayState
} from 'ngrx-forms';
import {
  Store,
  MemoizedSelector,
  createSelector
} from '@ngrx/store';
import { NGXLogger } from 'ngx-logger';
import { BASE_GROUP_STATE_SELECTOR } from './tokens';
import { Subscription } from 'rxjs';
import {
  filter,
  distinctUntilChanged,
  tap
} from 'rxjs/operators';

@Directive({
  selector: '[dxFormArray]'
})
export class DxFormArrayDirective extends NgForOf<FormGroupState<any>>
  implements OnInit, OnDestroy {
  @Input() public dxFormArray: string;

  public readonly baseSelector: MemoizedSelector<any, FormGroupState<any>>;

  private _subscriptions = new Subscription();

  constructor(
    viewContainer: ViewContainerRef,
    template: TemplateRef<NgForOfContext<FormGroupState<any>>>,
    differs: IterableDiffers,
    public readonly store$: Store<any>,
    public readonly logger: NGXLogger,
    @Inject(BASE_GROUP_STATE_SELECTOR) baseSelector: any
  ) {
    super(viewContainer, template, differs);
    this.baseSelector = baseSelector;
  }

  public ngOnInit(): void {
    this.ngForTrackBy = (_, item) => item.id;

    this.logger.trace(
      `[FormArrayDirective::ngOnInit::${this.dxFormArray}] call`
    );

    const controlSelector: MemoizedSelector<
      any,
      FormArrayState<any>
    > = createSelector(
      this.baseSelector,
      baseState => this.applyControlPath(baseState, this.dxFormArray)
    );

    this._subscriptions.add(
      this.store$
        .select(controlSelector)
        .pipe(
          filter(Boolean),
          distinctUntilChanged(),
          tap(state => (this.ngForOf = state.controls as any[]))
        )
        .subscribe()
    );
  }

  private applyControlPath(
    baseState: FormGroupState<any>,
    controlPath: string
  ): FormArrayState<any> {
    this.logger.trace(
      `[DxFormArrayDirective::ngOnInit::${controlPath}] baseState`,
      baseState
    );
    const targetState: FormArrayState<any> = controlPath
      .split('/')
      .reduce((state, controlId) => {
        if (state.controls.hasOwnProperty(controlId)) {
          const newState = state.controls[controlId];
          this.logger.trace(
            `[DxFormArrayDirective::ngOnInit::${controlPath}] reduce state`,
            { controlId, newState }
          );
          return newState;
        }
        this.logger.error(
          `[DxFormArrayDirective::ngOnInit::${controlPath}] control path is not valid`,
          { dxFormControl: controlPath, baseState }
        );
        throw new Error(
          `[DxFormArrayDirective::ngOnInit::${controlPath}] control path is not valid`
        );
      }, baseState) as any;
    this.logger.trace(
      `[DxFormArrayDirective::ngOnInit::${controlPath}] targetState`,
      targetState
    );

    if (!targetState.controls || !Array.isArray(targetState.controls)) {
      throw new Error(
        `[DxFormArrayDirective::ngOnInit::${controlPath}] targetState is not an FormArrayState`
      );
    }

    return targetState as FormArrayState<any>;
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
    this._subscriptions = new Subscription();
  }
}
